import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
  CircularProgress,
} from "@mui/material";
import {
  OrderOut,
  useWebApiV1RejectOrderMutation,
} from "@providers/hop-ord-server/api";
import {
  useCommentsApiV1PostCommentMutation,
  useProtocolApiV1ListDataQuery,
} from "@providers/hop-ord-server/api";
import { SubmitHandler, useForm } from "react-hook-form";
import {
  AutocompleteElement,
  FormContainer,
  TextFieldElement,
} from "react-hook-form-mui";
import { parseListData } from "@utils";
import { ListDataCategory } from "@enums";
import { useMemo, useState } from "react";

const OTHER = "Other";

export interface InputTypes {
  rejectReason: string[];
  additionalInfo: string;
}

interface Props {
  open: boolean;
  setOpen: (value: boolean) => void;
  order?: OrderOut;
  onReject: (success: boolean) => void;
}

const RejectOrderModal = ({ open, setOpen, order, onReject }: Props) => {
  const [rejectOrder] = useWebApiV1RejectOrderMutation();
  const [addNewComment] = useCommentsApiV1PostCommentMutation();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const categories = [ListDataCategory.REJECT_REASON];
  const { data: listData } = useProtocolApiV1ListDataQuery({
    categories,
    tenantIds: [order?.tenantId ?? -1],
  });

  const listOptions = useMemo(
    () => parseListData(listData || [], categories),
    [listData],
  );
  const rejectReasonOptions = listOptions[ListDataCategory.REJECT_REASON];

  const form = useForm<InputTypes>({
    defaultValues: {
      rejectReason: [],
      additionalInfo: "",
    },
    mode: "all",
  });

  const submitPayload: SubmitHandler<InputTypes> = (payload) => {
    // Check everything is valid
    setIsSubmitting(true);
    rejectOrder({
      orderId: Number(order?.id),
      rejectOrderIn: {
        rejectReason: payload.rejectReason,
        rejectedAdditionalInfo: payload.additionalInfo,
      },
    })
      .then(() => {
        if (!order?.orderGroupId) return;
        const content = `Rejected plan because of the following reason(s): ${payload.rejectReason.join(
          ", ",
        )}${payload.additionalInfo ? "," : ""} ${payload.additionalInfo}`;
        addNewComment({
          orderGroupId: order?.orderGroupId,
          commentIn: {
            content,
            orderId: Number(order?.id),
          },
        });
        setOpen(false);
        onReject(true);
      })
      .catch(() => {
        onReject(false);
      })
      .finally(() => {
        setIsSubmitting(false);
      });
  };

  const isAdditionalInfoMandatory = form.watch("rejectReason").includes(OTHER);

  return (
    <Dialog open={open}>
      <FormContainer
        formContext={form}
        handleSubmit={form.handleSubmit(submitPayload)}
      >
        <DialogTitle data-testid="dialog-title">Reject plan</DialogTitle>
        <DialogContent data-testid="dialog-content">
          <Stack spacing={2}>
            <Typography
              variant="body1"
              data-testid="dialog-content-description"
            >
              Please provide reasons for rejecting so the planning team can make
              the necessary amendments.
            </Typography>
            <AutocompleteElement
              label="Reject reasons"
              multiple={true}
              options={rejectReasonOptions}
              name="rejectReason"
              required={true}
              showCheckbox={true}
            />
            <TextFieldElement
              label="Additional information"
              name="additionalInfo"
              multiline
              rows={4}
              data-testid="dialog-content-additional-info"
              helperText="Please indicate the applicable plans if there are multiple"
              required={isAdditionalInfoMandatory}
              rules={{
                required: {
                  value: isAdditionalInfoMandatory,
                  message: "Please specify other rejection reason",
                },
              }}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button
            variant="text"
            color="primary"
            onClick={() => {
              setOpen(false);
            }}
            data-testid="reject-modal-cancel-btn"
          >
            Cancel
          </Button>
          <Button
            data-testid="reject-modal-reject-btn"
            variant="text"
            color="error"
            type="submit"
            sx={{ width: "76px" }}
          >
            {isSubmitting ? (
              <CircularProgress color="error" size={24} />
            ) : (
              "Reject"
            )}
          </Button>
        </DialogActions>
      </FormContainer>
    </Dialog>
  );
};

export default RejectOrderModal;
